import { ColumnsType } from 'antd/es/table';
import { RelationChangeLogType } from '../graphql/queries/getRelationChangeLogs';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import { OperationType } from '../enums/RelationLogEnums';
import dayjs from 'dayjs';

export const useRelationChangeLogColumns = (): ColumnsType<RelationChangeLogType> => {
    const { t } = useTranslation();

    const getOperationTypeTag = (operationType: number) => {
        switch (operationType) {
            case OperationType.CREATE:
                return <Tag color="green">{t('central.relationLogs.operationTypes.create')}</Tag>;
            case OperationType.UPDATE:
                return <Tag color="blue">{t('central.relationLogs.operationTypes.update')}</Tag>;
            case OperationType.DELETE:
                return <Tag color="red">{t('central.relationLogs.operationTypes.delete')}</Tag>;
            default:
                return <Tag>{operationType}</Tag>;
        }
    };

    const renderSingleData = (data: string | null | undefined, title: string) => {
        // 统一的容器样式
        const containerStyle = {
            padding: '6px', 
            fontSize: '11px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #e9ecef', 
            borderRadius: '3px',
            minHeight: '60px',
            maxHeight: '80px',
            overflowY: 'auto' as const,
            display: 'flex',
            alignItems: 'flex-start'
        };

        if (!data) {
            return (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        color: '#666', 
                        marginBottom: '4px' 
                    }}>
                        {title}
                    </div>
                    <div style={{ 
                        ...containerStyle,
                        color: '#999',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        -
                    </div>
                </div>
            );
        }

        try {
            const formattedJson = JSON.stringify(JSON.parse(data), null, 2);
            return (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        color: '#666', 
                        marginBottom: '4px' 
                    }}>
                        {title}
                    </div>
                    <pre style={{ 
                        ...containerStyle,
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word', 
                        margin: 0,
                        lineHeight: '1.3',
                        color: '#333'
                    }}>
                        {formattedJson}
                    </pre>
                </div>
            );
        } catch (e) {
            return (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        color: '#666', 
                        marginBottom: '4px' 
                    }}>
                        {title}
                    </div>
                    <div style={{ 
                        ...containerStyle,
                        wordBreak: 'break-word',
                        color: '#666'
                    }}>
                        {data}
                    </div>
                </div>
            );
        }
    };

    const renderDataChanges = (oldData: string | null | undefined, newData: string | null | undefined) => {
        if (!oldData && !newData) {
            return <span style={{ color: '#999' }}>-</span>;
        }

        return (
            <div style={{ 
                display: 'flex', 
                gap: '12px', 
                width: '100%',
                minHeight: '40px'
            }}>
                {renderSingleData(oldData, t('central.relationLogs.columns.oldData'))}
                {(oldData || newData) && (
                    <div style={{ 
                        width: '1px', 
                        backgroundColor: '#e9ecef', 
                        flexShrink: 0,
                        alignSelf: 'stretch'
                    }} />
                )}
                {renderSingleData(newData, t('central.relationLogs.columns.newData'))}
            </div>
        );
    };

    return [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            align: 'left',
        },
        {
            title: t('central.relationLogs.columns.entityType'),
            dataIndex: 'entityType',
            key: 'entityType',
            width: 120,
            align: 'left',
        },
        {
            title: t('central.relationLogs.columns.entityId'),
            dataIndex: 'entityId',
            key: 'entityId',
            width: 120,
            align: 'left',
        },
        {
            title: t('central.relationLogs.columns.entityName'),
            dataIndex: 'entityName',
            key: 'entityName',
            width: 150,
            align: 'left',
            render: (entityName: string | null | undefined) => {
                return entityName || <span style={{ color: '#999' }}>-</span>;
            },
        },
        {
            title: t('central.relationLogs.columns.relationType'),
            dataIndex: 'relationType',
            key: 'relationType',
            width: 200,
            align: 'left',
        },
        {
            title: t('central.relationLogs.columns.operationType'),
            dataIndex: 'operationType',
            key: 'operationType',
            width: 120,
            align: 'left',
            render: getOperationTypeTag,
        },
        {
            title: t('central.relationLogs.columns.operatorId'),
            dataIndex: 'operatorId',
            key: 'operatorId',
            width: 100,
            align: 'left',
            render: (operatorId: number | null | undefined) => {
                return operatorId || <span style={{ color: '#999' }}>-</span>;
            },
        },
        {
            title: t('central.relationLogs.columns.operatorRole'),
            dataIndex: 'operatorRole',
            key: 'operatorRole',
            width: 120,
            align: 'left',
            render: (operatorRole: string | null | undefined) => {
                return operatorRole || <span style={{ color: '#999' }}>-</span>;
            },
        },
        {
            title: t('central.relationLogs.columns.operationTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            align: 'left',
            render: (timestamp: number) => {
                return dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm');
            },
        },
        {
            title: t('central.relationLogs.columns.dataChanges'),
            key: 'dataChanges',
            width: 600,
            render: (_, record: RelationChangeLogType) => {
                return renderDataChanges(record.oldData, record.newData);
            },
        },
        {
            title: t('central.relationLogs.columns.note'),
            dataIndex: 'note',
            key: 'note',
            ellipsis: true,
            align: 'left',
            render: (note: string | null | undefined) => {
                return note || <span style={{ color: '#999' }}>-</span>;
            },
        },
    ];
};